import React, { createContext, useContext, useMemo } from 'react';

import type { CustodyAddress } from '../../components/move';

export type CustodyType = CustodyAddress['type'];
export type CustodyDirection = 'source' | 'destination';

/**
 * Configuration for disabling a custody type in forms.
 */
export type DisabledCustodyConfig = {
  custodyType: CustodyType;
  appliesTo: CustodyDirection;
  label?: string;
  details?: string;
};

export type DisabledCustodyItemInfo = Pick<
  DisabledCustodyConfig,
  'label' | 'details'
>;

export type DisabledCustodyCheckItem = {
  custody: CustodyType;
  walletId?: string;
};

export type GetDisabledCustodyConfigFn = (
  item: DisabledCustodyCheckItem,
  appliesTo: CustodyDirection
) => DisabledCustodyConfig | null;

// Context value type
type DisabledCustodyContextValue = {
  /**
   * Check if a custody type is disabled for a given direction.
   */
  isDisabled: (custody: CustodyType, direction?: CustodyDirection) => boolean;

  /**
   * Get the disabled config for a custody type if it's disabled.
   * Returns null if not disabled.
   */
  getDisabledConfigForCustody: (
    item: DisabledCustodyCheckItem,
    direction: CustodyDirection
  ) => DisabledCustodyConfig | null;

  /**
   * Get the disabled message for a custody type (convenience method).
   */
  getDisabledMessage: (
    custody: CustodyType,
    direction?: CustodyDirection
  ) => string | undefined;
};

/**
 * Default context value provides safe fallbacks when no provider is present.
 *
 * DESIGN DECISION: Components that use useDisabledCustodyConfig() outside of a
 * DisabledCustodyProvider will get these defaults, meaning NO custodies are disabled.
 *
 * This is intentional - only "signing flows" (Move, Send, Swidge, Perps, etc.)
 * should wrap in the provider. Non-signing UI (portfolio, asset details, activity)
 * should NOT have disabled custodies.
 */
const DisabledCustodyContext = createContext<DisabledCustodyContextValue>({
  isDisabled: () => false,
  getDisabledConfigForCustody: () => null,
  getDisabledMessage: () => undefined,
});

type DisabledCustodyProviderProps = {
  children: React.ReactNode;
  /**
   * Custody configuration for this flow.
   * Pass undefined or empty array to allow all custody types.
   */
  config?: DisabledCustodyConfig[] | GetDisabledCustodyConfigFn;
};

/**
 * Provider that supplies disabled custody configuration to child components.
 *
 * Use this in top-level flow components (SendDialog, MoveDialog, etc.) to
 * provide custody restrictions to all children without prop drilling.
 *
 * @example
 * // In MoveDialog (hardware wallets disabled):
 * <DisabledCustodyProvider config={[
 *   { custodyType: 'external-signer', appliesTo: 'source', label: 'Unavailable' }
 * ]}>
 *   <MoveFormProvider>
 *     <MoveContent />
 *   </MoveFormProvider>
 * </DisabledCustodyProvider>
 *
 * @example
 * // In SendDialog (hardware wallets enabled - no restrictions):
 * <DisabledCustodyProvider config={[]}>
 *   <SendFormProvider>
 *     <SendContent />
 *   </SendFormProvider>
 * </DisabledCustodyProvider>
 *
 * @example
 * // In child components - just use the hook:
 * const { isDisabled, getDisabledMessage } = useDisabledCustodyConfig();
 * if (isDisabled('external-signer')) {
 *   // Show disabled state with message
 * }
 */
export function DisabledCustodyProvider({
  children,
  config = [],
}: DisabledCustodyProviderProps) {
  const value = useMemo<DisabledCustodyContextValue>(() => {
    const getDisabledConfigForCustody = (
      item: DisabledCustodyCheckItem,
      direction: CustodyDirection
    ): DisabledCustodyConfig | null => {
      if (typeof config === 'function') {
        return config(item, direction);
      }
      const found = config.find(
        (c) => c.appliesTo === direction && c.custodyType === item.custody
      );
      return found ?? null;
    };

    const isDisabled = (
      custody: CustodyType,
      direction: CustodyDirection = 'source'
    ): boolean => {
      return getDisabledConfigForCustody({ custody }, direction) !== null;
    };

    const getDisabledMessage = (
      custody: CustodyType,
      direction: CustodyDirection = 'source'
    ): string | undefined => {
      const cfg = getDisabledConfigForCustody({ custody }, direction);
      return cfg?.label ?? cfg?.details;
    };

    return {
      isDisabled,
      getDisabledConfigForCustody,
      getDisabledMessage,
    };
  }, [config]);

  return (
    <DisabledCustodyContext.Provider value={value}>
      {children}
    </DisabledCustodyContext.Provider>
  );
}

/**
 * Hook to access disabled custody configuration from the nearest provider.
 *
 * @example
 * const { isDisabled, getDisabledConfigForCustody } = useDisabledCustodyConfig();
 *
 * // Check if hardware wallets are disabled
 * if (isDisabled('external-signer')) {
 *   const config = getDisabledConfigForCustody({ custody: 'external-signer' }, 'source');
 *   // Show disabled state
 * }
 */
export function useDisabledCustodyConfig(): DisabledCustodyContextValue {
  return useContext(DisabledCustodyContext);
}

/**
 * Pure function version - used when you need to check config outside React context.
 * In React components, prefer useDisabledCustody() hook.
 */
export function getDisabledConfigForCustodyItem(
  config: DisabledCustodyConfig[] | GetDisabledCustodyConfigFn | undefined,
  item: DisabledCustodyCheckItem,
  direction: CustodyDirection
): DisabledCustodyConfig | null {
  if (!config) return null;
  if (typeof config === 'function') return config(item, direction);
  const found = config.find(
    (c) => c.appliesTo === direction && c.custodyType === item.custody
  );
  return found ?? null;
}
